import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Gift, MapPin, HeartCrack, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useSavedUsers } from "@/hooks/useSavedUsers";
import { useToggleSavedUser } from "@/hooks/useToggleSavedUser";
import { formatDistanceToNow } from "date-fns";
import { calculateDistance } from "@/lib/distance";
import Spinner from "@/components/Spinner";

export default function SavedUsers() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const { data: savedUsers = [], isLoading } = useSavedUsers();
  const { mutate: toggleSaved } = useToggleSavedUser();

  const filteredUsers = savedUsers.filter((saved) =>
    saved.user?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSendGift = (userId: string) => {
    navigate(`/gift?user=${userId}`);
  };

  const handleRemove = (userId: string) => {
    toggleSaved({ userId, isSaved: true });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b pt-safe">
        <div className="container mx-auto px-4 pb-4">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="h-10 w-10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Saved Users</h1>
            <div className="w-10" />
          </div>
          <Input
            type="search"
            placeholder="Search saved users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-md md:max-w-2xl lg:max-w-4xl">
        <div className="mb-4 flex items-center justify-between">
          <p className="text-muted-foreground">
            {filteredUsers.length} {filteredUsers.length === 1 ? "person" : "people"} saved
          </p>
        </div>

        {filteredUsers.length === 0 ? (
          <Card className="p-12 text-center">
            <HeartCrack className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">
              {searchTerm ? "No users found" : "No saved users yet"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? "Try a different search term"
                : "Save users from the map to quickly send them gifts later"}
            </p>
            {!searchTerm && (
              <Button onClick={() => navigate("/map")}>
                <MapPin className="h-4 w-4 mr-2" />
                Explore Map
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredUsers.map((saved) => {
              const user = saved.user;
              if (!user) return null;

              const isOnline = user.location_updated_at
                ? new Date(user.location_updated_at).getTime() > Date.now() - 5 * 60 * 1000
                : false;

              const distance = user.lat && user.lng
                ? calculateDistance(0, 0, user.lat, user.lng)
                : null;

              return (
                <Card key={saved.id} className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={user.avatar_url || undefined} />
                      <AvatarFallback>
                        {user.name?.charAt(0).toUpperCase() || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{user.name || "Anonymous"}</h3>
                      {user.status && (
                        <p className="text-sm text-muted-foreground truncate">{user.status}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={isOnline ? "default" : "secondary"} className="text-xs">
                          {isOnline ? "Online" : "Offline"}
                        </Badge>
                        {distance && (
                          <span className="text-xs text-muted-foreground">
                            {distance.toFixed(1)}km away
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {saved.notes && (
                    <p className="text-sm text-muted-foreground mb-3 p-2 bg-muted rounded">
                      {saved.notes}
                    </p>
                  )}

                  <div className="text-xs text-muted-foreground mb-3">
                    Saved {formatDistanceToNow(new Date(saved.created_at), { addSuffix: true })}
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      {user.name?.toLowerCase().startsWith('ambassador') && (
                        <Button
                          onClick={() => navigate(`/prize-draw?user=${user.id}`)}
                          variant="outline"
                          size="sm"
                          className="flex-1"
                        >
                          <Ticket className="h-4 w-4 mr-1" />
                          Draw
                        </Button>
                      )}
                      <Button
                        onClick={() => handleSendGift(user.id)}
                        className="flex-1"
                        size="sm"
                      >
                        <Gift className="h-4 w-4 mr-1" />
                        Gift
                      </Button>
                      <Button
                        onClick={() => handleRemove(user.id)}
                        variant="outline"
                        size="sm"
                        aria-label="Remove saved user"
                      >
                        <HeartCrack className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};
